import styled from 'styled-components';
import { Empty, Input, Select, Spin } from 'antd';
import {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
} from 'react';

import { Colors } from 'basic-ui-lib/constants';
import { Text } from 'basic-ui-lib/components/Text';
import { IcCheck, IcInStore, IcTimeLine } from 'basic-ui-lib/icons';
import BaseModal from 'basic-ui-lib/components/Modal/BaseModal';
import useGetListStores from 'modules/store/services/hooks/useGetListStore';
import { Row } from 'basic-ui-lib';
import { formatDuration } from 'utils/time';
import { IApiGetPlaylistParam, IPlaylist } from 'modules/playlist/interfaces/api';
import playlistApis from 'modules/playlist/services/apis';
import { IResponseData } from 'services/response';
import { PaginationResponse } from 'services/commonTypes';

type Props = {
  currentStoreId?: string;
};

type Ref = {
  show: (
    currentPlaylistId: string,
    onConfirm: (playlistId: string) => void,
  ) => void;
  hide: () => void;
};

export type SelectPlaylistModalRef = Ref;

export const useSelectPlaylistModalRef = () => useRef<Ref>(null);

const SelectPlaylistModal = forwardRef<Ref, Props>(
  ({ currentStoreId }, ref) => {
    const [open, setOpen] = useState(false);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [selectedPlaylistId, setSelectedPlaylistId] = useState('');
    const [search, setSearch] = useState('');
    const [playlistsData, setPlaylistsData] = useState<IPlaylist[] | null>(null);
    const [loading, setLoading] = useState(false);

    const { storesList: storesData } = useGetListStores(true);

    const onConfirmRef = useRef<(playlistId: string) => void>(() => undefined);

    const fetchPlaylists = async (params: IApiGetPlaylistParam ) => {
      try {
        setLoading(true);
        const response: IResponseData<PaginationResponse<IPlaylist>> = await playlistApis.getListPlaylists(params);
        setPlaylistsData(response?.data?.data?.content ?? []);
      } catch (error) {
        console.error('Error fetching playlists:', error);
      } finally {
        setLoading(false);
      }
    };

    const storeOptions = useMemo(() => {
      return (storesData?.content ?? []).map((store) => ({
        label: store.storeName,
        value: store.storeId,
      }));
    }, [storesData]);

    const selectedStoreName = useMemo(() => {
      return storeOptions.find((store) => store.value === selectedStoreId)
        ?.label;
    }, [storeOptions, selectedStoreId]);

    const filteredPlaylists = useMemo(() => {
      const keyword = search.trim().toLowerCase();

      if (!keyword) {
        return playlistsData ?? [];
      }

      return playlistsData?.filter((playlist) =>
        playlist.name.toLowerCase().includes(keyword),
      );
    }, [playlistsData, search]);

    const onShow: Ref['show'] = (currentPlaylistId, onConfirm) => {
      setSelectedStoreId(currentStoreId ?? '');
      setSelectedPlaylistId(currentPlaylistId);
      setSearch('');
      fetchPlaylists({ page: 1, size: 100, storeId: currentStoreId });
      onConfirmRef.current = onConfirm;
      setOpen(true);
    };

    const onHide: Ref['hide'] = () => {
      setOpen(false);
    };

    useImperativeHandle(ref, () => ({
      show: onShow,
      hide: onHide,
    }));

    const handleStoreChange = (storeId: string) => {
      setSelectedStoreId(storeId);
      setSelectedPlaylistId('');
      setSearch('');
      fetchPlaylists({ page: 1, size: 100, storeId });
    };

    const handleConfirm = () => {
      if (!selectedPlaylistId) {
        return;
      }

      onConfirmRef.current(selectedPlaylistId);
      onHide();
    };

    const handleClose = () => {
      onHide();
    };

    return (
      <BaseModal
        title='Select Playlist'
        isOpen={open}
        width={800}
        onClose={handleClose}
        okText='Select Playlist'
        onOk={handleConfirm}
        disabled={!selectedPlaylistId}
      >
        <ModalContent>
          <StoreSection>
            <Row
              width='100%'
              gap={8}
              alignItems='center'
              justifyContent='center'
            >
              <IcInStore />
              <Text medium>Store</Text>
            </Row>
            <Select
              showSearch
              optionFilterProp='label'
              value={selectedStoreId || undefined}
              options={storeOptions}
              placeholder='Select a store'
              onChange={handleStoreChange}
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              style={{ width: '100%' }}
              size='large'
              popupMatchSelectWidth
            />
          </StoreSection>

          <SearchRow>
            <CustomSearch
              allowClear
              placeholder='Search playlist...'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              size='large'
            />
          </SearchRow>

          <ListHeader>
            <Text content_2 regular color='neutral.text.6'>
              Playlists ({loading ? '...' : filteredPlaylists?.length})
            </Text>

            <Row>
              <IcInStore width={20} height={20} />
              <Text content_2 medium color='neutral.text.6'>
                {selectedStoreName ?? 'No store selected'}
              </Text>
            </Row>
          </ListHeader>

          <PlaylistList>
            {loading ? (
              <LoadingContainer>
                <Spin />
                <Text body_1 regular color='neutral.text.6'>
                  Loading playlists...
                </Text>
              </LoadingContainer>
            ) : !selectedStoreId ? (
              <Empty description='Please select a store' />
            ) : filteredPlaylists?.length === 0 ? (
              <Empty
                description={
                  search
                    ? 'No playlists match your search'
                    : 'This store has no playlists yet'
                }
              />
            ) : (
              filteredPlaylists?.map((playlist) => {
                const isSelected = selectedPlaylistId === playlist.id;

                return (
                  <ItemPlaylist
                    key={playlist.id}
                    role='button'
                    tabIndex={0}
                    aria-pressed={isSelected}
                    className={isSelected ? 'active' : ''}
                    onClick={() => setSelectedPlaylistId(playlist.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedPlaylistId(playlist.id);
                      }
                    }}
                  >
                    <PlaylistThumbnail>
                      <PlaylistIcon>
                        <IcTimeLine width={32} height={32} />
                      </PlaylistIcon>
                    </PlaylistThumbnail>

                    <PlaylistInfo>
                      <Text title_h9 medium>
                        {playlist.name}
                      </Text>

                      <PlaylistMeta>
                        {playlist.contentItems != null && (
                          <Text caption_1 color='neutral.text.6'>
                            {playlist.contentItems.length} media
                          </Text>
                        )}

                        {playlist.duration != null && (
                          <MetaDot />
                        )}

                        {playlist.duration != null && (
                          <Text caption_1 color='neutral.text.6'>
                            Duration: {formatDuration(playlist.duration)}
                          </Text>
                        )}
                      </PlaylistMeta>

                      {playlist.lastModifiedDate && (
                        <Text caption_1 color='neutral.text.6'>
                          Updated {playlist.lastModifiedDate}
                        </Text>
                      )}
                    </PlaylistInfo>

                    <SelectionArea>
                      {isSelected ? (
                        <SelectedIcon>
                          <IcCheck
                            fill={Colors.primary[5]}
                            width={24}
                            height={24}
                          />
                        </SelectedIcon>
                      ) : (
                        <UnselectedIcon />
                      )}
                    </SelectionArea>
                  </ItemPlaylist>
                );
              })
            )}
          </PlaylistList>
        </ModalContent>
      </BaseModal>
    );
  },
);

SelectPlaylistModal.displayName = 'SelectPlaylistModal';

export default SelectPlaylistModal;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 24px 24px;
  min-height: 300px;

  @media (max-width: 768px) {
    padding: 0 0 16px;
    gap: 16px;
    min-height: 250px;
  }
`;

const StoreSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ant-select {
    height: 48px;
    .ant-select-selector {
      border: 1px solid ${Colors.neutral.border[2]} !important;
    }

    .ant-select-selection-item {
      font-size: 18px;
      font-weight: 600;
      font-family: 'Poppins', sans-serif;
    }

    .ant-select-arrow {
      .anticon {
        font-size: 20px;
      }
    }
  }
`;

const SearchRow = styled.div`
  width: 100%;
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: -8px;
`;

const PlaylistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  min-height: 180px;
  overflow-y: auto;
  padding: 2px 4px 4px 2px;

  @media (max-width: 768px) {
    max-height: 320px;
    min-height: 160px;
  }
`;

const ItemPlaylist = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 96px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${Colors.neutral.border[1]};
  background: ${Colors.neutral.bg[1]};
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: ${Colors.primary[4]};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.primary[5]};
    outline-offset: 2px;
  }

  &.active {
    border: 2px solid ${Colors.primary[6]};
    background: ${Colors.primary[1]};
    padding: 11px;
  }
`;

const PlaylistThumbnail = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 68px;
  flex-shrink: 0;
  border-radius: 6px;
  background: ${Colors.neutral.bg[2]};

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
  }
`;

const PlaylistIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.neutral.text[2]};
`;

const PlaylistInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  min-width: 0;s
`;

const PlaylistMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: ${Colors.neutral.text[2]};
  font-size: 12px;
`;

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${Colors.neutral.text[3]};
`;


const SelectionArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
`;

const SelectedIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  border: 1px solid ${Colors.primary[5]};
`;

const UnselectedIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid ${Colors.neutral.border[2]};
  border-radius: 50%;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  min-height: 180px;
`;

const CustomSearch = styled(Input.Search)`
  .ant-input-affix-wrapper {
    height: 48px;
    padding: 0 12px;
  }

  .ant-input {
    font-size: 18px!important;
  }

  .ant-input-search-button {
    height: 48px;
    width: 48px;
  }

  .ant-input-search-button .anticon {
    font-size: 20px;
  }

  .ant-input-suffix {
    .anticon {
      font-size: 20px;
    }
  }
`;